import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
// Imports for loading & configuring the in-memory web api
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { LoggerModule, NGXLogger, NgxLoggerLevel } from 'ngx-logger';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { PageService } from './models/page.service';
import { FlowModule } from './flow/flow.module';
import { TopModule } from './component/top/top.module';
import { ItemsModule } from './items/items.module';
import { NotFoundComponent } from './component/notfound/notfound.component';
import { LogResolve } from './log.resolve';
import { LoginComponent } from './component/login/login.component';
import { PasswordComponent } from './component/password/password.component';
import { PasswordService } from './component/password/password.service';
import { AuComponent } from './component/au/au.component';
import { AuAction } from './action/au.action';
import { CommonAction } from './action/common.action';
import { ApiAction } from './action/api.action';
import { ReflectionAction } from './action/reflection.action';
import { ConditionAction } from './action/condition.action';
import { BulkAction } from './action/bulk.action';
import { ContentComponent } from './component/content/content.component';
import { AuthenticationService } from './service/authentication.service';
import { AlertService } from './service/alert.service';
import { AuthGuard } from './guard/auth.guard';
import { RoleGuard } from './guard/role.guard';
import { PassGuard } from './guard/pass.guard';
import { ApiService } from './service/api.service';
import { LoadingComponent } from './component/loading/loading.component';
import { LoadingService } from './component/loading/loading.service';
import { AlertModalComponent } from './component/alertmodal/alertmodal.component';
import { AlertModalService } from './component/alertmodal/alertmodal.service';
import { AlertBlockComponent } from './component/alertblock/alertblock.component';
import { AlertBlockService } from './component/alertblock/alertblock.service';
import { MessageService } from './service/message.service';
import { UnitService } from './service/unit.service';
import { ConfigService } from './service/config.service';
import { ClassService } from './service/class.service';
import { ValidationService } from './service/validation.service';
import { PopStateService } from './service/popstate.service';
import { ConditionService } from './service/condition.service';
import { LogService } from './service/log.service';
import { BaseService } from './component/base/base.service';
import { ContentBaseService } from './component/base/contentbase.service';
import { HeaderComponent } from './component/header/header.component';
import { FooterComponent } from './component/footer/footer.component';
import { MenuComponent, MenuGroupComponent } from './component/menu/menu.component';
import { MenuService } from './component/menu/menu.service';
import { GridComponent } from './component/grid/grid.component';
import { GridDirective } from './component/grid/grid.directive';
import { DivComponent } from './component/div/div.component';
import { DivDirective } from './component/div/div.directive';
import { FilterComponent } from './component/filter/filter.component';
import { FilterDirective } from './component/filter/filter.directive';
import { HrComponent } from './component/hr/hr.component';
import { LabelComponent } from './component/label/label.component';
import { NaviComponent } from './component/navi/navi.component';
import { ButtonComponent } from './component/button/button.component';
import { ButtonService } from './component/button/button.service';
import { EntryComponent } from './component/entry/entry.component';
import { ViewComponent } from './component/view/view.component';
import { EditMessagesComponent } from './component/editmessages/editmessages.component';
import { SearchComponent } from './component/list/search.component';
import { ListCountComponent } from './component/list/count.component';
import { RowsComponent } from './component/list/rows.component';
import { ListComponent, ListRowGroupComponent } from './component/list/list.component';
import { PagerComponent } from './component/list/pager.component';
import { ListGroupComponent } from './component/list/listgroup.component';
import { CsvComponent } from './component/list/csv.component';
import { BulkComponent } from './component/list/bulk.component';
import { ListService } from './component/list/list.service';
import { ListDirective } from './component/list/list.directive';
import { LanguageComponent } from './component/language/language.component';
import { LanguageService } from './component/language/language.service';
import { TreeComponent } from './component/tree/tree.component';
import { TreeService } from './component/tree/tree.service';
import { CurrentUnitComponent } from './component/unit/currentunit.component';
import { CurrentUnitService } from './component/unit/currentunit.service';
import { UnitComponent } from './component/unit/unit.component';
import { UnitTreeService } from './component/unit/unit.service';
import { AccessUnitComponent } from './component/unit/accessunit.component';
import { AccessUnitService } from './component/unit/accessunit.service';
import { ScopeComponent } from './component/unit/scope.component';
import { ScopeService } from './component/unit/scope.service';
import { MemberSearchService } from './component/member_search/member_search.service';
import { MemberSearchComponent } from './component/member_search/member_search.component';
import { MemberSearchSearchComponent } from './component/member_search/search.component';
import { MemberSearchConditionListComponent } from './component/member_search/conditionlist.component';
import { MemberSearchConditionComponent } from './component/member_search/condition.component';
import { MemberSearchConditionGroupComponent } from './component/member_search/conditiongroup.component';
import { MemberSearchRowsComponent } from './component/member_search/rows.component';
import { MemberSearchListComponent } from './component/member_search/list.component';
import { MemberSearchPagerComponent } from './component/member_search/pager.component';
import { CampaignService } from './component/campaign/campaign.service';
import { CampaignNaviComponent, CampaignNaviGroupComponent } from './component/campaign/campaign-navi.component';
import { CampaignStepComponent } from './component/campaign/campaign-step.component';
import { CampaignGoalComponent } from './component/campaign/campaign-goal.component';
import { CampaignSegmentComponent } from './component/campaign/campaign-segment.component';
import { CampaignMeasureComponent } from './component/campaign/campaign-measure.component';
import { CampaignContentsComponent } from './component/campaign/campaign-contents.component';
import { CampaignTimingComponent } from './component/campaign/campaign-timing.component';
import { CampaignConfirmComponent } from './component/campaign/campaign-confirm.component';
import { CampaignReportComponent } from './component/campaign/campaign-report.component';
import { ItemSubItemComponent } from './component/item/item-subitem.component';
import { ItemSubItemSelect2Component } from './component/item/item-subitem-select2.component';
import { ItemSubItemService } from './component/item/item-subitem.service';
import { ItemSubItemDirective } from './component/item/item-subitem.directive';
import { ItemModalComponent } from './component/item/item-modal.component';
import { ItemModalSearchComponent } from './component/item/item-modal-search.component';
import { ItemModalListCountComponent } from './component/item/item-modal-count.component';
import { ItemModalRowsComponent } from './component/item/item-modal-rows.component';
import { ItemModalListComponent } from './component/item/item-modal-list.component';
import { ItemModalPagerComponent } from './component/item/item-modal-pager.component';
import { ItemModalService } from './component/item/item-modal.service';
import { ItemCategoryTreeComponent } from './component/item/item-category-tree.component';
import { ItemTreeService } from './component/item/item-tree.service';
import { ItemCategoryTreeCheckComponent } from './component/item/item-category-tree-check.component';
import { ItemAddRowDirective } from './component/item/item-addrow.directive';
import { ItemPeriodComponent } from './component/item/item-period.component';
import { ItemPeriodService } from './component/item/item-period.service';
import { ItemPriceComponent } from './component/item/item-price.component';
import { ItemPriceService } from './component/item/item-price.service';
import { ItemImageComponent } from './component/item/item-image.component';
import { ItemImageService } from './component/item/item-image.service';
import { SortComponent } from './component/sort/sort.component';
import { SortTreeComponent, SortTreeGroupComponent } from './component/sort/sort-tree.component';
import { SortTreeService } from './component/sort/sort-tree.service';
import { TabGroupComponent } from './component/tab/tab.component';
import { TabGroupDirective } from './component/tab/tab.directive';
import { TabService } from './component/tab/tab.service';
import { InquiryComponent } from './component/inquiry/inquiry.component';
import { RegistryCondComponent } from './component/registry/registry-cond.component';
import { RegistryCondService } from './component/registry/registry-cond.service';
import { RegistryPointComponent } from './component/registry/registry-point.component';
import { RegistryPointService } from './component/registry/registry-point.service';
import { QuestionnaireComponent } from './component/questionnaire/questionnaire.component';
import { QuestionnairePanelComponent } from './component/questionnaire/questionnaire-panel.component';
import { QuestionnaireElementComponent } from './component/questionnaire/questionnaire-element.component';
import { QuestionnaireService } from './component/questionnaire/questionnaire.service';
import { QuestionnaireDirective } from './component/questionnaire/questionnaire.directive';
import { QuestionnaireAnswerComponent } from './component/questionnaire/questionnaire-answer.component';
import { MailDeliveryComponent } from './component/mail_delivery/mail-delivery.component';
import { MailDeliveryService } from './component/mail_delivery/mail-delivery.service';
import { OneTimeLoginService } from './component/onetimelogin/onetimelogin.service';
import { OneTimeLoginComponent } from './component/onetimelogin/onetimelogin.component';
import { OneTimeGuard } from './guard/onetime.guard';
import { QuestionnaireAggregateComponent } from './component/questionnaire/questionnaire-aggregate.component';
import { TemplateComponent } from './component/template/template.component';
import { TemplateValueComponent } from './component/template/template-value.component';
import { TemplateValueService } from './component/template/template-value.service';
import { ImportErrorComponent } from './component/import/import-error.component';
import { AddRowComponent } from './component/addrow/addrow.component';
import { AddRowService } from './component/addrow/addrow.service';
import { AddRowDirective } from './component/addrow/addrow.directive';
import { ToggleComponent } from './component/toggle/toggle.component';
import { ModalComponent } from './component/modal/modal.component';
import { MultiUploadComponent } from './component/upload/multi-upload.component';
import { MultiUploadFileComponent } from './component/upload/multi-upload-file.component';
//// kts
import { KtsVerificationComponent } from './projects/kts/component/verification/verification.component';
import { KtsMultiSelectComponent } from './projects/kts/component/multiselect/multiselect.component';
import { KtsMultiSelectService } from './projects/kts/component/multiselect/multiselect.service';
import { KtsMultiSelectPartnerCompanyComponent } from './projects/kts/component/multiselect/multiselect-partner-company.component';
import { KtsMultiSelectPartnerCompanyService } from './projects/kts/component/multiselect/multiselect-partner-company.service';
import { KtsMultiSelectPartnerUserComponent } from './projects/kts/component/multiselect/multiselect-partner-user.component';
import { KtsMultiSelectPartnerUserService } from './projects/kts/component/multiselect/multiselect-partner-user.service';
import { KtsMultiSelectConstructionWorkerComponent } from './projects/kts/component/multiselect/multiselect-construction-worker.component';
import { KtsMultiSelectConstructionWorkerService } from './projects/kts/component/multiselect/multiselect-construction-worker.service';
import { KtsTaskComponent } from './projects/kts/component/task/task.component';
import { KtsTaskService } from './projects/kts/component/task/task.service';
import { KtsTaskFieldComponent } from './projects/kts/component/task/task-field.component';
import { KtsConstructionTaskComponent } from './projects/kts/component/task/construction-task.component';
import { KtsConstructionTaskService } from './projects/kts/component/task/construction-task.service';
import { KtsConstructionTaskVisitComponent } from './projects/kts/component/task/construction-task-visit.component';
import { KtsGeneralTaskComponent } from './projects/kts/component/task/general-task.component';
import { KtsGeneralTaskService } from './projects/kts/component/task/general-task.service';
import { KtsImportTargetComponent } from './projects/kts/component/import/import-target.component';
import { KtsImportTargetService } from './projects/kts/component/import/import-target.service';
import { KtsProgressComponent } from './projects/kts/component/progress/progress.component';
import { KtsReportComponent } from './projects/kts/component/report/report.component';
import { KtsReportContentsComponent } from './projects/kts/component/report/report-contents.component';
import { KtsReportFileComponent } from './projects/kts/component/report/report-file.component';
import { KtsReportFileUploadComponent } from './projects/kts/component/report/report-file-upload.component';
import { KtsReportService } from './projects/kts/component/report/report.service';
import { KtsSearchComponent } from './projects/kts/component/list/search.component';
import { KtsOutlineComponent } from './projects/kts/component/outline/outline.component';
import { KtsOutlineService } from './projects/kts/component/outline/outline.service';
import { KtsOutlineFieldComponent } from './projects/kts/component/outline/outline-field.component';
import { KtsConstructionOutlineComponent } from './projects/kts/component/outline/construction-outline.component';
import { KtsSupervisorAddRowComponent } from './projects/kts/component/supervisor/supervisor-addrow.component';
import { KtsSupervisorComponent } from './projects/kts/component/supervisor/supervisor.component';
import { KtsConstructionMaterialComponent } from './projects/kts/component/task/construction-material.component';
import { KtsGeneralConstructionMaterialComponent } from './projects/kts/component/task/general-construction-material.component';
import { KtsConstructionMaterialService } from './projects/kts/component/task/construction-material.service';
import { KtsConstructionMaterialListComponent } from './projects/kts/component/task/construction-material-list.component';
import { KtsGeneralConstructionMaterialListComponent } from './projects/kts/component/task/general-construction-material-list.component';
import { KtsGanttComponent } from './projects/kts/component/gantt/gantt.component';
import { KtsGeneralGanttComponent } from './projects/kts/component/gantt/general-gantt.component';
import { KtsReportDownloadComponent } from './projects/kts/component/report/report-download.component';
import { KtsReportDownloadListComponent } from './projects/kts/component/report/report-download-list.component';
import { KtsInformationClaimStatusSelectComponent } from './projects/kts/component/information/information-claim-status-select.component';
import { KtsInformationClaimStatusSelectService } from './projects/kts/component/information/information-claim-status-select.service';
import { KtsReceptionContentComponent } from './projects/kts/component/reception/reception-content.component';
import { KtsConstructionTypeComponent } from './projects/kts/component/constructiontype/construction-type.component';
import { KtsConstructionTypeService } from './projects/kts/component/constructiontype/construction-type.service';
import { KtsPartnerAreaComponent } from './projects/kts/component/partner/partner-area.component';
import { KtsMapComponent } from './projects/kts/component/map/map.component';
import { KtsMultiUploadComponent } from './projects/kts/component/upload/multi-upload.component';
import { KtsMultiUploadFileComponent } from './projects/kts/component/upload/multi-upload-file.component';
import { KtsMaintenanceReceptionTimeComponent } from './projects/kts/component/maintenance/maintenance-reception-time.component';
import { KtsMaintenanceReceptionTimeService } from './projects/kts/component/maintenance/maintenance-reception-time.service';
export function configFactory(cs) {
    return function () { return cs.load(); };
}
export function classFactory(cs) {
    return function () { return cs.load(); };
}
var AppModule = /** @class */ (function () {
    function AppModule() {
    }
    AppModule.decorators = [
        { type: NgModule, args: [{
                    imports: [
                        BrowserModule,
                        FormsModule,
                        HttpClientModule,
                        HttpClientXsrfModule.withOptions({
                            cookieName: 'csrfToken',
                            headerName: 'Csrf-Token'
                        }),
                        AppRoutingModule,
                        LoggerModule.forRoot({
                            serverLogLevel: NgxLoggerLevel.OFF,
                            level: NgxLoggerLevel.ERROR
                        }),
                        LazyLoadImageModule,
                        TopModule,
                        FlowModule,
                        ItemsModule
                    ],
                    declarations: [
                        //// kts
                        KtsVerificationComponent,
                        KtsMultiSelectComponent,
                        KtsMultiSelectPartnerCompanyComponent,
                        KtsMultiSelectPartnerUserComponent,
                        KtsMultiSelectConstructionWorkerComponent,
                        KtsTaskComponent,
                        KtsTaskFieldComponent,
                        KtsConstructionTaskComponent,
                        KtsConstructionTaskVisitComponent,
                        KtsGeneralTaskComponent,
                        KtsImportTargetComponent,
                        KtsProgressComponent,
                        KtsReportComponent,
                        KtsReportContentsComponent,
                        KtsReportFileComponent,
                        KtsReportFileUploadComponent,
                        KtsSearchComponent,
                        KtsOutlineComponent,
                        KtsOutlineFieldComponent,
                        KtsConstructionOutlineComponent,
                        KtsSupervisorAddRowComponent,
                        KtsSupervisorComponent,
                        KtsConstructionMaterialComponent,
                        KtsGeneralConstructionMaterialComponent,
                        KtsConstructionMaterialListComponent,
                        KtsGeneralConstructionMaterialListComponent,
                        KtsGanttComponent,
                        KtsGeneralGanttComponent,
                        KtsReportDownloadComponent,
                        KtsReportDownloadListComponent,
                        KtsInformationClaimStatusSelectComponent,
                        KtsReceptionContentComponent,
                        KtsConstructionTypeComponent,
                        KtsPartnerAreaComponent,
                        KtsMapComponent,
                        KtsMultiUploadComponent,
                        KtsMultiUploadFileComponent,
                        KtsMaintenanceReceptionTimeComponent,
                        //// ma31
                        AppComponent,
                        AuComponent,
                        NotFoundComponent,
                        LoginComponent,
                        PasswordComponent,
                        OneTimeLoginComponent,
                        ContentComponent,
                        LoadingComponent,
                        AlertModalComponent,
                        AlertBlockComponent,
                        HeaderComponent,
                        FooterComponent,
                        MenuComponent,
                        MenuGroupComponent,
                        GridComponent,
                        GridDirective,
                        DivComponent,
                        DivDirective,
                        FilterComponent,
                        FilterDirective,
                        HrComponent,
                        LabelComponent,
                        NaviComponent,
                        ButtonComponent,
                        EntryComponent,
                        ViewComponent,
                        EditMessagesComponent,
                        SearchComponent,
                        ListCountComponent,
                        RowsComponent,
                        ListComponent,
                        ListRowGroupComponent,
                        PagerComponent,
                        ListGroupComponent,
                        CsvComponent,
                        BulkComponent,
                        ListDirective,
                        LanguageComponent,
                        TreeComponent,
                        CurrentUnitComponent,
                        UnitComponent,
                        AccessUnitComponent,
                        ScopeComponent,
                        MemberSearchComponent,
                        MemberSearchSearchComponent,
                        MemberSearchConditionListComponent,
                        MemberSearchConditionComponent,
                        MemberSearchConditionGroupComponent,
                        MemberSearchRowsComponent,
                        MemberSearchListComponent,
                        MemberSearchPagerComponent,
                        CampaignNaviComponent,
                        CampaignNaviGroupComponent,
                        CampaignStepComponent,
                        CampaignGoalComponent,
                        CampaignSegmentComponent,
                        CampaignMeasureComponent,
                        CampaignContentsComponent,
                        CampaignTimingComponent,
                        CampaignConfirmComponent,
                        CampaignReportComponent,
                        ItemSubItemComponent,
                        ItemSubItemSelect2Component,
                        ItemSubItemDirective,
                        ItemModalComponent,
                        ItemModalSearchComponent,
                        ItemModalListCountComponent,
                        ItemModalRowsComponent,
                        ItemModalListComponent,
                        ItemModalPagerComponent,
                        ItemCategoryTreeComponent,
                        ItemCategoryTreeCheckComponent,
                        ItemAddRowDirective,
                        ItemPeriodComponent,
                        ItemPriceComponent,
                        ItemImageComponent,
                        SortComponent,
                        SortTreeComponent,
                        SortTreeGroupComponent,
                        TabGroupComponent,
                        TabGroupDirective,
                        InquiryComponent,
                        RegistryCondComponent,
                        RegistryPointComponent,
                        QuestionnaireComponent,
                        QuestionnairePanelComponent,
                        QuestionnaireElementComponent,
                        QuestionnaireDirective,
                        QuestionnaireAnswerComponent,
                        QuestionnaireAggregateComponent,
                        MailDeliveryComponent,
                        TemplateComponent,
                        TemplateValueComponent,
                        ImportErrorComponent,
                        AddRowComponent,
                        AddRowDirective,
                        ToggleComponent,
                        ModalComponent,
                        MultiUploadComponent,
                        MultiUploadFileComponent
                    ],
                    entryComponents: [
                        //// kts
                        KtsVerificationComponent,
                        KtsMultiSelectComponent,
                        KtsMultiSelectPartnerCompanyComponent,
                        KtsMultiSelectPartnerUserComponent,
                        KtsMultiSelectConstructionWorkerComponent,
                        KtsTaskComponent,
                        KtsTaskFieldComponent,
                        KtsConstructionTaskComponent,
                        KtsConstructionTaskVisitComponent,
                        KtsGeneralTaskComponent,
                        KtsImportTargetComponent,
                        KtsProgressComponent,
                        KtsReportComponent,
                        KtsReportContentsComponent,
                        KtsReportFileComponent,
                        KtsReportFileUploadComponent,
                        KtsSearchComponent,
                        KtsOutlineComponent,
                        KtsOutlineFieldComponent,
                        KtsConstructionOutlineComponent,
                        KtsSupervisorAddRowComponent,
                        KtsSupervisorComponent,
                        KtsConstructionMaterialComponent,
                        KtsGeneralConstructionMaterialComponent,
                        KtsConstructionMaterialListComponent,
                        KtsGeneralConstructionMaterialListComponent,
                        KtsGanttComponent,
                        KtsGeneralGanttComponent,
                        KtsReportDownloadComponent,
                        KtsReportDownloadListComponent,
                        KtsInformationClaimStatusSelectComponent,
                        KtsReceptionContentComponent,
                        KtsConstructionTypeComponent,
                        KtsPartnerAreaComponent,
                        KtsMapComponent,
                        KtsMultiUploadComponent,
                        KtsMultiUploadFileComponent,
                        KtsMaintenanceReceptionTimeComponent,
                        //// ma31
                        GridComponent,
                        DivComponent,
                        FilterComponent,
                        HrComponent,
                        LabelComponent,
                        NaviComponent,
                        ButtonComponent,
                        EntryComponent,
                        ViewComponent,
                        EditMessagesComponent,
                        SearchComponent,
                        ListCountComponent,
                        RowsComponent,
                        ListComponent,
                        ListRowGroupComponent,
                        PagerComponent,
                        ListGroupComponent,
                        CsvComponent,
                        BulkComponent,
                        TreeComponent,
                        UnitComponent,
                        AccessUnitComponent,
                        ScopeComponent,
                        MemberSearchComponent,
                        MemberSearchSearchComponent,
                        MemberSearchConditionListComponent,
                        MemberSearchConditionComponent,
                        MemberSearchConditionGroupComponent,
                        MemberSearchRowsComponent,
                        MemberSearchListComponent,
                        MemberSearchPagerComponent,
                        CampaignNaviComponent,
                        CampaignNaviGroupComponent,
                        CampaignStepComponent,
                        CampaignGoalComponent,
                        CampaignSegmentComponent,
                        CampaignMeasureComponent,
                        CampaignContentsComponent,
                        CampaignTimingComponent,
                        CampaignConfirmComponent,
                        CampaignReportComponent,
                        ItemSubItemComponent,
                        ItemCategoryTreeComponent,
                        ItemCategoryTreeCheckComponent,
                        ItemPeriodComponent,
                        ItemPriceComponent,
                        ItemImageComponent,
                        SortComponent,
                        SortTreeComponent,
                        SortTreeGroupComponent,
                        TabGroupComponent,
                        InquiryComponent,
                        RegistryCondComponent,
                        RegistryPointComponent,
                        QuestionnaireComponent,
                        QuestionnairePanelComponent,
                        QuestionnaireElementComponent,
                        QuestionnaireAnswerComponent,
                        QuestionnaireAggregateComponent,
                        MailDeliveryComponent,
                        TemplateComponent,
                        TemplateValueComponent,
                        ImportErrorComponent,
                        AddRowComponent,
                        ToggleComponent,
                        ModalComponent,
                        MultiUploadComponent,
                        MultiUploadFileComponent
                    ],
                    providers: [
                        //// kts
                        KtsMultiSelectService,
                        KtsMultiSelectPartnerCompanyService,
                        KtsMultiSelectPartnerUserService,
                        KtsMultiSelectConstructionWorkerService,
                        KtsTaskService,
                        KtsConstructionTaskService,
                        KtsGeneralTaskService,
                        KtsImportTargetService,
                        KtsReportService,
                        KtsOutlineService,
                        KtsConstructionMaterialService,
                        KtsInformationClaimStatusSelectService,
                        KtsConstructionTypeService,
                        KtsMaintenanceReceptionTimeService,
                        //// ma31
                        PageService,
                        LogResolve,
                        NGXLogger,
                        AuthenticationService,
                        AlertService,
                        PasswordService,
                        OneTimeLoginService,
                        AuthGuard,
                        RoleGuard,
                        PassGuard,
                        OneTimeGuard,
                        ApiService,
                        LoadingService,
                        AlertModalService,
                        AlertBlockService,
                        AuAction,
                        CommonAction,
                        ApiAction,
                        ReflectionAction,
                        ConditionAction,
                        BulkAction,
                        MessageService,
                        UnitService,
                        ConfigService,
                        ClassService,
                        ValidationService,
                        PopStateService,
                        ConditionService,
                        LogService,
                        BaseService,
                        ContentBaseService,
                        MenuService,
                        ButtonService,
                        ListService,
                        LanguageService,
                        TreeService,
                        CurrentUnitService,
                        UnitTreeService,
                        AccessUnitService,
                        ScopeService,
                        MemberSearchService,
                        CampaignService,
                        ItemSubItemService,
                        ItemModalService,
                        ItemPeriodService,
                        ItemPriceService,
                        ItemImageService,
                        SortTreeService,
                        TabService,
                        RegistryCondService,
                        RegistryPointService,
                        QuestionnaireService,
                        MailDeliveryService,
                        TemplateValueService,
                        AddRowService,
                        {
                            provide: APP_INITIALIZER,
                            'useFactory': configFactory,
                            deps: [ConfigService],
                            multi: true
                        },
                        {
                            provide: APP_INITIALIZER,
                            'useFactory': classFactory,
                            deps: [ClassService],
                            multi: true
                        },
                        // {
                        // 	provide: Http,
                        // 	useFactory: (backend: XHRBackend, options: RequestOptions, router: Router) => {
                        // 		return new CustomHttp(backend, options);
                        // 	},
                        // 	deps: [XHRBackend, RequestOptions, Router]
                        // }
                        ItemTreeService
                    ],
                    bootstrap: [AppComponent]
                },] }
    ];
    return AppModule;
}());
export { AppModule };
//# sourceMappingURL=app.module.js.map